import React, { useState, useEffect } from 'react';
import { Form, Button, Modal } from 'react-bootstrap';
import { IconBell, IconX } from '@tabler/icons-react';
import NumberFormat from 'react-number-format';
import { CAPABILITIES, TIME_OPTIONS, INTEGRATIONS } from '../../../constants';
import DynamicConfigField from '../DynamicConfigField';
import GoogleSheetsCapabilityPicker from '../GoogleSheetsCapabilityPicker';
import SchedulingCalendarPicker from '../SchedulingCalendarPicker';
import InternalCalendarHoursPicker from '../InternalCalendarHoursPicker';
import CrmPipelinePicker from '../CrmPipelinePicker';
import SheetsReaderPicker from '../SheetsReaderPicker';
import RemindersModal from '../RemindersModal';
import DescriptionTemplateGuideModal from '../../../components/DescriptionTemplateGuideModal';
import AgentsFlowService from '../../../services/AgentsFlowService';

// Mesma normalização do KnowledgeTab — mantém em sync com backend.
const normalizeCategoryTag = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  let s = raw.normalize('NFKD').replace(/\p{Mark}/gu, '');
  s = s.trim().toLowerCase();
  s = s.replace(/[\s\-]+/g, '_');
  s = s.replace(/[^a-z0-9_]/g, '');
  s = s.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  return s;
};

/**
 * Picker de filtro de categorias para o nó FAQ.
 * Vazio = busca em todos os documentos (cross-cutting + taggeados).
 * Preenchido = busca em docs cuja categories ∩ filtro ≠ ∅ + docs sem tag.
 */
const FaqCategoryFilterPicker = ({ agentId, selected, onChange }) => {
  const [available, setAvailable] = useState([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!agentId || agentId === 'novo') return;
    AgentsFlowService.getKnowledgeCategories(agentId)
      .then((resp) => setAvailable(resp.data || []))
      .catch(() => setAvailable([]));
  }, [agentId]);

  const current = Array.isArray(selected) ? selected : [];
  const suggestions = available
    .filter((s) => !current.includes(s) && s.includes(normalizeCategoryTag(inputValue)))
    .slice(0, 8);

  const addTag = (raw) => {
    const norm = normalizeCategoryTag(raw);
    if (!norm || norm.startsWith('__') || current.includes(norm)) return;
    onChange([...current, norm]);
    setInputValue('');
  };

  const removeTag = (tag) => {
    onChange(current.filter((t) => t !== tag));
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label className="afc-form-label">Filtrar por categorias</Form.Label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {current.map((t) => (
          <span key={t} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#e0f2fe', color: '#0369a1', padding: '4px 10px',
            borderRadius: 12, fontSize: 12, fontWeight: 500,
          }}>
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
            >
              <IconX size={11} />
            </button>
          </span>
        ))}
      </div>
      <Form.Control
        type="text"
        size="sm"
        placeholder="Digite uma categoria e Enter (ex: cardiologia)"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addTag(inputValue);
          }
        }}
        onBlur={() => {
          if (inputValue.trim()) addTag(inputValue);
        }}
      />
      {inputValue && suggestions.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <span style={{ fontSize: 11, color: '#666', marginRight: 4 }}>Sugestões:</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              // onMouseDown + preventDefault: o input tem onBlur que comita
              // o texto digitado parcial. Sem isso, o blur dispara antes do
              // click, criando uma tag com o input incompleto e o re-render
              // remove o botão antes do click registrar.
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
              style={{
                background: '#f3f4f6', border: '1px solid #e5e7eb',
                padding: '2px 8px', borderRadius: 10, fontSize: 11, cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
        Deixe vazio para buscar em todos os documentos. Documentos sem tag (cross-cutting) sempre aparecem.
      </Form.Text>
    </Form.Group>
  );
};

const formatPhoneMask = (value) => {
  const clean = value.replace(/\D/g, '');
  if (clean.length === 0) return '';
  if (clean.length <= 2) return `(${clean}`;
  if (clean.length <= 6) return clean.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (clean.length <= 10) return clean.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return clean.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

/**
 * CapabilityFields - Renders configuration fields for capability and integration node types.
 *
 * Handles:
 *  - media_library (custom UI with media items)
 *  - All other capabilities via CAPABILITIES constant + DynamicConfigField
 *  - Integration nodes via INTEGRATIONS constant + DynamicConfigField
 *
 * Includes modals for temperature help and stage rules help.
 */
const CapabilityFields = ({
  nodeKey,
  nodeType,
  localConfig,
  handleConfigChange,
  agentId,
  integrations,
  mediaList,
  mediaLoading,
  renderDefaultPlaceholder,
}) => {
  const [showReminders, setShowReminders] = useState(false);
  const remindersList = Array.isArray(localConfig.reminders) ? localConfig.reminders : [];
  // ========== INTEGRATIONS (Dynamic from INTEGRATIONS constant) ==========
  if (nodeType === 'integration') {
    const integrationConfig = INTEGRATIONS[nodeKey];

    if (integrationConfig && integrationConfig.configFields && integrationConfig.configFields.length > 0) {
      return (
        <>
          {integrationConfig.description && (
            <div className="afc-capability-description mb-3">
              <small className="text-muted">{integrationConfig.description}</small>
            </div>
          )}
          {integrationConfig.configFields.map((field) => (
            <DynamicConfigField
              key={field.key}
              field={field}
              value={localConfig[field.key]}
              onChange={handleConfigChange}
              allValues={localConfig}
              TIME_OPTIONS_LIST={TIME_OPTIONS}
            />
          ))}
        </>
      );
    }

    return renderDefaultPlaceholder('integração');
  }

  // ========== CAPABILITIES ==========
  // Custom UI for media_library
  if (nodeKey === 'media_library') {
    const mediaItems = localConfig.media_items || [];
    const addMediaItem = (mediaId) => {
      if (!mediaId) return;
      // Check if already added
      if (mediaItems.find(m => m.media_id === mediaId)) return;
      const updated = [...mediaItems, { media_id: mediaId, instruction: '', providers: ['whatsapp', 'site'], caption: '' }];
      handleConfigChange('media_items', updated);
    };
    const removeMediaItem = (mediaId) => {
      const updated = mediaItems.filter(m => m.media_id !== mediaId);
      handleConfigChange('media_items', updated);
    };
    const updateMediaItem = (mediaId, field, value) => {
      const updated = mediaItems.map(m => m.media_id === mediaId ? { ...m, [field]: value } : m);
      handleConfigChange('media_items', updated);
    };
    const toggleProvider = (mediaId, provider) => {
      const item = mediaItems.find(m => m.media_id === mediaId);
      if (!item) return;
      const providers = item.providers || ['whatsapp', 'site'];
      const updated = providers.includes(provider) ? providers.filter(p => p !== provider) : [...providers, provider];
      if (updated.length === 0) return; // must have at least one
      updateMediaItem(mediaId, 'providers', updated);
    };

    return (
      <>
        <div className="afc-capability-description mb-3">
          <small className="text-muted">Configure instruções por mídia e a IA envia automaticamente imagens, PDFs ou áudios quando a conversa indicar.</small>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">Instrução geral</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            className="afc-form-control"
            defaultValue={localConfig.general_instruction || 'Envie mídias apenas quando relevante para a conversa'}
            onChange={(e) => handleConfigChange('general_instruction', e.target.value)}
            placeholder="Ex: Envie mídias apenas quando relevante para a conversa..."
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">Máximo de envios por conversa</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={10}
            className="afc-form-control"
            value={localConfig.max_sends_per_conversation || 3}
            onChange={(e) => handleConfigChange('max_sends_per_conversation', parseInt(e.target.value) || 3)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">Adicionar mídia</Form.Label>
          {mediaLoading ? (
            <div className="text-muted small">Carregando mídias...</div>
          ) : (
            <Form.Select
              className="afc-form-control"
              value=""
              onChange={(e) => addMediaItem(e.target.value)}
            >
              <option value="">Selecionar mídia...</option>
              {(mediaList || [])
                .filter(m => !mediaItems.find(mi => mi.media_id === m.id))
                .map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.media_type === 'image' ? 'Imagem' : m.media_type === 'audio' ? 'Áudio' : 'PDF'})
                  </option>
                ))}
            </Form.Select>
          )}
        </Form.Group>

        {mediaItems.length > 0 && (
          <div className="mb-3">
            <Form.Label className="afc-form-label">Mídias configuradas ({mediaItems.length})</Form.Label>
            {mediaItems.map((item) => {
              const mediaInfo = (mediaList || []).find(m => m.id === item.media_id);
              const mediaName = mediaInfo?.name || item.media_id?.substring(0, 8);
              const mediaType = mediaInfo?.media_type || 'desconhecido';
              const mediaUrl = mediaInfo?.url;
              return (
                <div key={item.media_id} style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px', marginBottom: '10px', background: '#f8f9fa' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      {mediaType === 'image' && mediaUrl && (
                        <img src={mediaUrl} alt={mediaName} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                      )}
                      <div>
                        <strong style={{ fontSize: '0.9em' }}>{mediaName}</strong>
                        <div style={{ fontSize: '0.75em', color: '#6c757d' }}>
                          {mediaType === 'image' ? 'Imagem' : mediaType === 'audio' ? 'Áudio' : 'PDF'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeMediaItem(item.media_id)}
                      title="Remover"
                    >
                      &times;
                    </button>
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8em', fontWeight: 500 }}>Quando enviar</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      style={{ fontSize: '0.85em' }}
                      className="afc-form-control"
                      value={item.instruction || ''}
                      onChange={(e) => updateMediaItem(item.media_id, 'instruction', e.target.value)}
                      placeholder="Ex: Quando o cliente perguntar sobre preços..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8em', fontWeight: 500 }}>Legenda (opcional)</Form.Label>
                    <Form.Control
                      type="text"
                      style={{ fontSize: '0.85em' }}
                      className="afc-form-control"
                      value={item.caption || ''}
                      onChange={(e) => updateMediaItem(item.media_id, 'caption', e.target.value)}
                      placeholder="Ex: Confira nosso catálogo, {{client_name}}!"
                    />
                  </Form.Group>

                  <div className="d-flex gap-3" style={{ fontSize: '0.8em' }}>
                    <Form.Check
                      type="checkbox"
                      label="WhatsApp"
                      checked={(item.providers || []).includes('whatsapp')}
                      onChange={() => toggleProvider(item.media_id, 'whatsapp')}
                    />
                    <Form.Check
                      type="checkbox"
                      label="Site"
                      checked={(item.providers || []).includes('site')}
                      onChange={() => toggleProvider(item.media_id, 'site')}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  const capabilityConfig = CAPABILITIES[nodeKey];

  if (capabilityConfig && capabilityConfig.configFields && capabilityConfig.configFields.length > 0) {
    return (
      <>
        {capabilityConfig.longDescription && (
          <div className="afc-capability-description mb-3">
            <small className="text-muted">{capabilityConfig.longDescription}</small>
          </div>
        )}
        {capabilityConfig.configFields.map((field) => (
          <React.Fragment key={field.key}>
            <DynamicConfigField
              field={field}
              value={localConfig[field.key]}
              onChange={handleConfigChange}
              allValues={localConfig}
              TIME_OPTIONS_LIST={TIME_OPTIONS}
            />
            {field.key === 'destination' && localConfig.destination === 'google_sheets' && (
              <GoogleSheetsCapabilityPicker
                agentId={agentId}
                integrations={integrations}
                config={localConfig}
                onChange={handleConfigChange}
              />
            )}
            {field.key === 'crm_management_enabled' && localConfig.crm_management_enabled
              && localConfig.destination === 'internal_crm' && (
              <CrmPipelinePicker
                config={localConfig}
                onChange={handleConfigChange}
              />
            )}
            {field.key === 'spreadsheet_id' && nodeKey === 'sheets_reader' && (
              <SheetsReaderPicker
                agentId={agentId}
                integrations={integrations}
                config={localConfig}
                onChange={handleConfigChange}
              />
            )}
            {field.key === 'whatsapp_number' && localConfig.forward_to_whatsapp && (
              <Form.Group className="mb-3">
                <Form.Label className="afc-form-label">Número do WhatsApp</Form.Label>
                <NumberFormat
                  className="form-control afc-form-control"
                  format={formatPhoneMask}
                  mask="_"
                  placeholder="(00) 00000-0000"
                  value={localConfig.whatsapp_number || ''}
                  onValueChange={(values) => {
                    handleConfigChange('whatsapp_number', values.value);
                  }}
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Número que receberá o resumo dos emails (com DDD)
                </Form.Text>
              </Form.Group>
            )}
            {field.key === 'calendar_provider' && localConfig.calendar_provider === 'google_calendar' && nodeKey === 'scheduling' && (
              <SchedulingCalendarPicker
                agentId={agentId}
                integrations={integrations}
                config={localConfig}
                onChange={handleConfigChange}
              />
            )}
            {field.key === 'calendar_provider' && (!localConfig.calendar_provider || localConfig.calendar_provider === 'internal') && nodeKey === 'scheduling' && (
              <InternalCalendarHoursPicker
                calendarId={localConfig.internal_calendar_id || null}
                onCalendarChange={(id) => handleConfigChange('internal_calendar_id', id)}
              />
            )}
          </React.Fragment>
        ))}

        {/* Lembretes automáticos — só no nó scheduling */}
        {nodeKey === 'scheduling' && (
          <div className="mt-3 w-100">
            <Button
              variant="warning"
              size="sm"
              onClick={() => setShowReminders(true)}
              className="d-inline-flex align-items-center gap-2 w-100"
            >
              <IconBell size={14} />
              Lembretes
              {remindersList.length > 0 && (
                <span className="badge bg-warning text-dark">
                  {remindersList.length}
                </span>
              )}
            </Button>
            <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
              Notificações automáticas via WhatsApp ou e-mail antes do horário do agendamento.
            </div>
          </div>
        )}

        {/* Filtro de categorias da base de conhecimento — só no nó FAQ */}
        {(nodeKey === 'faq' || nodeKey === 'answer_faq') && (
          <div className="mt-3">
            <FaqCategoryFilterPicker
              agentId={agentId}
              selected={localConfig.filter_categories || []}
              onChange={(list) => handleConfigChange('filter_categories', list)}
            />
          </div>
        )}

        <RemindersModal
          show={showReminders}
          onClose={() => setShowReminders(false)}
          onSave={(reminders) => handleConfigChange('reminders', reminders)}
          initialReminders={remindersList}
        />

        {/* Modal explicativo: Instruções de descrição do agendamento */}
        <DescriptionTemplateGuideModal
          show={!!localConfig._showHelp_description_instructions}
          onHide={() => handleConfigChange('_showHelp_description_instructions', false)}
        />

        {/* Modal explicativo: Regras de Temperatura */}
        <Modal
          show={!!localConfig._showHelp_temperature}
          onHide={() => handleConfigChange('_showHelp_temperature', false)}
          centered
        >
          <Modal.Header closeButton style={{ borderBottom: '2px solid #6366f1' }}>
            <Modal.Title style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>🌡️</span> Como funcionam as Regras de Temperatura
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: 14 }}>
            <p>
              <strong>Temperatura</strong> é uma forma de medir o quanto um cliente está interessado em comprar.
              Pense como um termômetro:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '16px 0' }}>
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <strong style={{ color: '#2563eb' }}>🧊 Frio (cold)</strong>
                <p className="mb-0 mt-1" style={{ fontSize: 13 }}>
                  O cliente só está perguntando, sem compromisso nenhum. Exemplo: "Quais serviços vocês oferecem?"
                </p>
              </div>
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                <strong style={{ color: '#d97706' }}>🔥 Morno (warm)</strong>
                <p className="mb-0 mt-1" style={{ fontSize: 13 }}>
                  O cliente demonstrou interesse real — perguntou preço, prazo, comparou opções. Exemplo: "Quanto custa o plano mensal?"
                </p>
              </div>
              <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                <strong style={{ color: '#dc2626' }}>🔥🔥 Quente (hot)</strong>
                <p className="mb-0 mt-1" style={{ fontSize: 13 }}>
                  O cliente quer fechar! Já disse que quer comprar, contratar ou agendar. Exemplo: "Quero contratar o plano premium!"
                </p>
              </div>
            </div>

            <p className="fw-semibold mb-1">O que escrever no campo?</p>
            <p style={{ fontSize: 13 }}>
              Escreva instruções simples para a IA saber como classificar. Cada linha é uma regra.
              A IA vai ler a conversa com o cliente e seguir essas regras para decidir se o lead é frio, morno ou quente.
            </p>

            <div className="p-2 rounded" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 13 }}>
              <strong>Exemplo prático:</strong><br />
              <code style={{ whiteSpace: 'pre-wrap', color: '#166534' }}>
                {`- Se apenas pediu informações → frio\n- Se perguntou preço ou prazo → morno\n- Se disse que quer fechar → quente`}
              </code>
            </div>

            <p className="text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
              A IA vai usar a ferramenta <em>update_contact_crm</em> automaticamente para classificar o contato no CRM.
              O cliente nunca verá essa classificação — é tudo interno.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" size="sm" onClick={() => handleConfigChange('_showHelp_temperature', false)}>
              Entendi!
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal explicativo: Regras de Movimentação de Estágio */}
        <Modal
          show={!!localConfig._showHelp_stage}
          onHide={() => handleConfigChange('_showHelp_stage', false)}
          centered
        >
          <Modal.Header closeButton style={{ borderBottom: '2px solid #6366f1' }}>
            <Modal.Title style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>📋</span> Como funcionam os Estágios do Pipeline
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: 14 }}>
            <p>
              O <strong>pipeline</strong> é como um quadro com colunas (tipo Trello ou Kanban).
              Cada coluna é um <strong>estágio</strong> que representa onde o cliente está na jornada de compra.
            </p>

            <p className="fw-semibold mb-2">Exemplo visual:</p>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '4px 0', margin: '8px 0 16px' }}>
              {[
                { name: 'Novo', emoji: '📥', color: '#eff6ff', border: '#bfdbfe', desc: 'Acabou de chegar' },
                { name: 'Negociação', emoji: '🤝', color: '#fffbeb', border: '#fde68a', desc: 'Está conversando' },
                { name: 'Ganho', emoji: '🎉', color: '#f0fdf4', border: '#bbf7d0', desc: 'Fechou negócio!' },
                { name: 'Perdido', emoji: '😞', color: '#fef2f2', border: '#fecaca', desc: 'Não quis comprar' },
              ].map((stage) => (
                <div key={stage.name} style={{
                  flex: '1 1 0', minWidth: 100, padding: 10, borderRadius: 8,
                  backgroundColor: stage.color, border: `1px solid ${stage.border}`,
                  textAlign: 'center', fontSize: 12
                }}>
                  <div style={{ fontSize: 20 }}>{stage.emoji}</div>
                  <strong>{stage.name}</strong>
                  <div className="text-muted" style={{ fontSize: 11 }}>{stage.desc}</div>
                </div>
              ))}
            </div>

            <p>
              A IA lê a conversa e <strong>move o contato</strong> de uma coluna para outra automaticamente,
              seguindo as regras que você escreveu.
            </p>

            <p className="fw-semibold mb-1">O que escrever no campo?</p>
            <p style={{ fontSize: 13 }}>
              Escreva instruções dizendo quando a IA deve mover o contato para cada estágio.
              Use os mesmos nomes de estágio que existem no seu pipeline do CRM.
            </p>

            <div className="p-2 rounded" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 13 }}>
              <strong>Exemplo prático:</strong><br />
              <code style={{ whiteSpace: 'pre-wrap', color: '#166534' }}>
                {`- Contato novo → manter no estágio inicial\n- Pediu proposta → mover para "Negociação"\n- Confirmou compra → mover para "Ganho"\n- Desistiu → mover para "Perdido"`}
              </code>
            </div>

            <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', fontSize: 12 }}>
              <strong>Dica importante:</strong> Os nomes dos estágios nas regras devem ser <strong>iguais</strong> aos nomes
              das colunas do pipeline que você selecionou acima. Se o estágio se chama "Em negociação",
              escreva exatamente "Em negociação" na regra.
            </div>

            <p className="text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
              A IA usa a ferramenta <em>move_contact_stage</em> para mover o contato.
              Tudo acontece nos bastidores — o cliente não percebe nada.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" size="sm" onClick={() => handleConfigChange('_showHelp_stage', false)}>
              Entendi!
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return renderDefaultPlaceholder('capacidade');
};

export default CapabilityFields;
